import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CatalogFormProps, FormContext, FormFieldConfig, SelectOption } from './types';
import { useCatalogForm, isFieldVisible } from './useCatalogForm';

// Helper to normalize search text (lowercase + strip accents)
const normalizeSearchText = (text: string) =>
  (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

function AutocompleteField({
  field,
  value,
  error,
  disabled,
  submitting,
  onChange,
  onBlur,
  isRequired,
  colClass,
}: {
  field: FormFieldConfig;
  value: any;
  error?: string;
  disabled?: boolean;
  submitting?: boolean;
  onChange: (val: any) => void;
  onBlur: () => void;
  isRequired?: boolean;
  colClass: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [dynamicOptions, setDynamicOptions] = React.useState<SelectOption[]>(field.options || []);
  const [loading, setLoading] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const optionsListRef = React.useRef<HTMLDivElement>(null);
  const searchTimeoutRef = React.useRef<any>(null);
  const searchCacheRef = React.useRef<Record<string, SelectOption[]>>({});
  const activeRequestRef = React.useRef<number>(0);
  const lastSearchedRef = React.useRef<string>('');

  const minSearchLength = field.minSearchLength !== undefined ? field.minSearchLength : 2;
  const debounceMs = field.debounceMs !== undefined ? field.debounceMs : 350;

  // Selected option state preserved independently of search results
  const [selectedOption, setSelectedOption] = React.useState<SelectOption | null>(() => {
    if (value === undefined || value === null || value === '') return null;
    return (field.options || []).find((opt) => String(opt.value) === String(value)) || null;
  });

  // Keep selectedOption in sync when value changes or options are updated
  React.useEffect(() => {
    if (value === undefined || value === null || value === '') {
      setSelectedOption(null);
      return;
    }
    const match =
      dynamicOptions.find((opt) => String(opt.value) === String(value)) ||
      (field.options || []).find((opt) => String(opt.value) === String(value));
    if (match) {
      setSelectedOption(match);
    }
  }, [value, dynamicOptions, field.options]);

  // Sync external options ONLY when user is not actively searching
  React.useEffect(() => {
    if (field.options && (!field.onSearch || searchTerm.trim().length === 0)) {
      setDynamicOptions(field.options);
    }
  }, [field.options, field.onSearch, searchTerm]);

  // Display text in input
  const displayLabel = selectedOption
    ? selectedOption.label
    : (field.getOptionLabel ? field.getOptionLabel(value) : (value ? String(value) : ''));

  // Close dropdown on click outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setSearchTerm(text);
    setOpen(true);

    if (field.onSearch) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

      const normalized = text.trim().toLowerCase();

      // Safety guard 1: Don't saturate backend if query is too short
      if (normalized.length < minSearchLength) {
        setLoading(false);
        if (normalized.length === 0) {
          setDynamicOptions(field.options || []);
        }
        return;
      }

      // Safety guard 2: Use in-memory cache to prevent duplicate backend queries
      if (searchCacheRef.current[normalized]) {
        setDynamicOptions(searchCacheRef.current[normalized]);
        setLoading(false);
        return;
      }

      // Safety guard 3: Skip if same term was just fetched
      if (normalized === lastSearchedRef.current) {
        return;
      }

      // Safety guard 4: Debounce and race-condition prevention
      setLoading(true);
      searchTimeoutRef.current = setTimeout(async () => {
        const requestId = ++activeRequestRef.current;
        lastSearchedRef.current = normalized;

        try {
          if (field.onSearch) {
            const results = await field.onSearch(text.trim());
            // Only update if this request is still the newest one
            if (requestId === activeRequestRef.current && results && Array.isArray(results)) {
              searchCacheRef.current[normalized] = results;
              setDynamicOptions(results);
            }
          }
        } finally {
          if (requestId === activeRequestRef.current) {
            setLoading(false);
          }
        }
      }, debounceMs);
    }
  };

  const handleSelect = (opt: SelectOption) => {
    setSelectedOption(opt);
    onChange(opt.value);
    setSearchTerm('');
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOption(null);
    onChange('');
    setSearchTerm('');
  };

  // Filter options strictly matching what the user typed in the input
  const visibleOptions = React.useMemo(() => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return dynamicOptions;
    }
    const term = normalizeSearchText(searchTerm.trim());
    return dynamicOptions.filter((opt) => {
      const labelText = normalizeSearchText(opt.label || '');
      const valueText = opt.value !== undefined && opt.value !== null ? normalizeSearchText(String(opt.value)) : '';
      return labelText.includes(term) || valueText.includes(term);
    });
  }, [dynamicOptions, searchTerm]);

  const scrollToIndex = (index: number) => {
    if (optionsListRef.current) {
      const optionElement = optionsListRef.current.children[index] as HTMLElement;
      if (optionElement && optionElement.scrollIntoView) {
        optionElement.scrollIntoView({ block: 'nearest' });
      }
    }
  };

  // Keep highlighted index in sync when options or open state changes
  React.useEffect(() => {
    if (!open) {
      setHighlightedIndex(-1);
    } else {
      const selectedIdx = visibleOptions.findIndex((opt) => String(opt.value) === String(value));
      setHighlightedIndex(selectedIdx >= 0 ? selectedIdx : 0);
    }
  }, [open, visibleOptions, value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
        return;
      }
    }

    if (visibleOptions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev < visibleOptions.length - 1 ? prev + 1 : 0;
        scrollToIndex(next);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev > 0 ? prev - 1 : visibleOptions.length - 1;
        scrollToIndex(next);
        return next;
      });
    } else if (e.key === 'Enter') {
      if (open && highlightedIndex >= 0 && highlightedIndex < visibleOptions.length) {
        e.preventDefault();
        handleSelect(visibleOptions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Tab') {
      if (open && highlightedIndex >= 0 && highlightedIndex < visibleOptions.length) {
        handleSelect(visibleOptions[highlightedIndex]);
      }
    }
  };

  return (
    <div key={field.name} className={`${colClass} relative`} ref={containerRef}>
      <label htmlFor={field.name} className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
        {isRequired && <span className="text-red-500 mr-1">*</span>}
        {field.label}
      </label>
      <div className="relative">
        <input
          id={field.name}
          name={field.name}
          type="text"
          value={open ? searchTerm : (displayLabel || searchTerm)}
          placeholder={field.placeholder || `Buscar ${field.label.toLowerCase()}...`}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setSearchTerm('');
            setOpen(true);
          }}
          onBlur={onBlur}
          disabled={disabled || submitting}
          autoComplete="off"
          className={`w-full px-3 py-1.5 pr-10 min-h-[38px] text-sm text-gray-900 bg-white border rounded-md transition-colors focus:outline-hidden focus:ring-2 ${
            error
              ? 'border-red-400 focus:ring-red-400/20 focus:border-red-500'
              : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'
          } ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && (
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          )}
          {value && !disabled && !submitting && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Limpiar selección"
              className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg className="w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown Options */}
      {open && (
        <div
          ref={optionsListRef}
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {searchTerm.trim().length > 0 && searchTerm.trim().length < minSearchLength ? (
            <div className="p-3 text-xs text-gray-400 text-center">
              Escriba al menos {minSearchLength} caracteres para buscar...
            </div>
          ) : visibleOptions.length === 0 ? (
            <div className="p-3 text-xs text-gray-500 text-center">
              {loading ? 'Buscando...' : 'No se encontraron resultados'}
            </div>
          ) : (
            visibleOptions.map((opt, i) => {
              const isSelected = String(opt.value) === String(value);
              const isHighlighted = i === highlightedIndex;
              return (
                <button
                  key={`${opt.value}-${i}`}
                  type="button"
                  onMouseEnter={() => setHighlightedIndex(i)}
                  onClick={() => handleSelect(opt)}
                  className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors flex items-center justify-between cursor-pointer ${
                    isHighlighted
                      ? 'bg-blue-100 text-blue-900 font-medium'
                      : isSelected
                      ? 'bg-blue-50/70 font-semibold text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="truncate">{opt.label}</span>
                  {isSelected && (
                    <svg className="w-4 h-4 text-blue-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}

      {error && <p className="mt-1 text-xs font-medium text-red-500">{error}</p>}
      {field.helperText && !error && <p className="mt-1 text-xs text-gray-500">{field.helperText}</p>}
    </div>
  );
}

function getColSpanClass(col?: number): string {
  switch (col) {
    case 1:
      return 'col-span-12 sm:col-span-3 md:col-span-1';
    case 2:
      return 'col-span-12 sm:col-span-6 md:col-span-2';
    case 3:
      return 'col-span-12 sm:col-span-6 md:col-span-3';
    case 4:
      return 'col-span-12 sm:col-span-6 md:col-span-4';
    case 5:
      return 'col-span-12 sm:col-span-6 md:col-span-5';
    case 6:
      return 'col-span-12 md:col-span-6';
    case 7:
      return 'col-span-12 md:col-span-7';
    case 8:
      return 'col-span-12 md:col-span-8';
    case 9:
      return 'col-span-12 md:col-span-9';
    case 10:
      return 'col-span-12 md:col-span-10';
    case 11:
      return 'col-span-12 md:col-span-11';
    case 12:
    default:
      return 'col-span-12';
  }
}

export function CatalogForm<T extends Record<string, any>>({
  title,
  subtitle,
  fields,
  initialValues,
  id,
  loadData,
  onSubmit,
  onSuccess,
  onCancel,
  validate,
  backUrl,
  className = '',
  submitButtonText,
  headerActions,
  showBackButton = false,
}: CatalogFormProps<T>) {
  const history = useHistory();
  const routeParams = (useParams() as Record<string, any>) || {};
  const resolvedId = id !== undefined && id !== null ? id : (routeParams?.id || null);

  const handleSuccess = (res: any) => {
    if (onSuccess) {
      onSuccess(res);
    } else if (backUrl) {
      setTimeout(() => {
        history.push(backUrl);
      }, 500);
    }
  };

  const handleCancelClick = () => {
    if (onCancel) {
      onCancel();
    } else if (backUrl) {
      history.push(backUrl);
    } else {
      history.goBack();
    }
  };

  const {
    values,
    errors,
    touched,
    loading,
    submitting,
    statusMessage,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useCatalogForm<T>({
    initialValues,
    fields,
    id: resolvedId,
    loadData,
    onSubmit,
    onSuccess: handleSuccess,
    validate,
  });

  if (loading) {
    return (
      <div className="w-full flex flex-col items-center justify-center p-12 min-h-[300px]">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-500 font-medium text-sm">Cargando datos del registro...</p>
      </div>
    );
  }

  return (
    <div className={`w-full max-w-5xl mx-auto ${className}`}>
      {/* Top Header with Back Navigation (optional) */}
      {showBackButton && (
        <div className="flex items-center justify-between mb-2.5">
          <button
            type="button"
            onClick={handleCancelClick}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 bg-white hover:bg-gray-50 border border-gray-200 rounded-md shadow-2xs transition-colors min-h-[34px] cursor-pointer"
            aria-label="Regresar"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Regresar</span>
          </button>

          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-50 px-2.5 py-0.5 rounded-md border border-gray-100">
            {resolvedId ? `Editando #${resolvedId}` : 'Nuevo Registro'}
          </span>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-4 sm:p-5">
        {/* Title & Header Actions */}
        <div className="mb-3 pb-2 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">{title}</h2>
              {!showBackButton && (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100">
                  {resolvedId ? `Editando #${resolvedId}` : 'Nuevo Registro'}
                </span>
              )}
            </div>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          {headerActions && (
            <div className="flex items-center gap-2 flex-wrap">
              {headerActions}
            </div>
          )}
        </div>

        {/* Status Notification Banner */}
        {statusMessage && (
          <div
            className={`mb-4 p-3 rounded-lg text-sm flex items-start gap-2.5 transition-all ${
              statusMessage.type === 'error'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}
            role="alert"
          >
            {statusMessage.type === 'error' ? (
              <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            <div className="flex-1 font-medium text-xs">{statusMessage.text}</div>
          </div>
        )}

        {/* Form Grid */}
        <form onSubmit={handleSubmit} noValidate>
          {(() => {
            const context: FormContext = { id: resolvedId, isEdit: Boolean(resolvedId) };
            const visibleFields = fields.filter((f) => isFieldVisible(f, values, context));

            return (
              <div className="grid grid-cols-12 gap-x-3.5 gap-y-3">
                {visibleFields.map((field) => {
                  const colClass = getColSpanClass(field.col);
                  const fieldError = touched[field.name] ? errors[field.name] : undefined;
                  const isRequired = field.required || (field.validators && field.validators.length > 0);
                  const fieldValue = values[field.name] !== undefined && values[field.name] !== null ? values[field.name] : '';

                  // Section Header
                  if (field.type === 'section') {
                    const isFirstVisible = visibleFields[0]?.name === field.name;
                    return (
                      <div
                        key={field.name}
                        className={`col-span-12 ${
                          isFirstVisible
                            ? 'mt-0 pt-0 pb-0.5'
                            : 'mt-2 pt-2.5 pb-0.5 border-t border-gray-200/80'
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="w-1 h-3.5 bg-blue-600 rounded-xs inline-block" />
                          <h3 className="text-sm font-bold text-gray-800 tracking-tight">{field.label}</h3>
                        </div>
                        {field.helperText && <p className="text-xs text-gray-500 ml-2.5 mt-0.5">{field.helperText}</p>}
                      </div>
                    );
                  }

                  // Custom render
                  if (field.render) {
                    return (
                      <div key={field.name} className={colClass}>
                        {field.render({
                          value: fieldValue,
                          error: fieldError,
                          onChange: (val) => handleChange(field.name, val),
                          onBlur: () => handleBlur(field.name),
                          disabled: field.disabled || submitting,
                          values,
                        })}
                      </div>
                    );
                  }

                  // Checkbox
                  if (field.type === 'checkbox') {
                    return (
                      <div key={field.name} className={`${colClass} flex items-center min-h-[38px] self-end pb-1`}>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            id={field.name}
                            name={field.name}
                            checked={Boolean(fieldValue)}
                            onChange={(e) => handleChange(field.name, e.target.checked)}
                            onBlur={() => handleBlur(field.name)}
                            disabled={field.disabled || submitting}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                          />
                          <span className="text-xs font-medium text-gray-700">{field.label}</span>
                        </label>
                      </div>
                    );
                  }

                  // Textarea
                  if (field.type === 'textarea') {
                    return (
                      <div key={field.name} className={colClass}>
                        <label htmlFor={field.name} className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
                          {isRequired && <span className="text-red-500 mr-1">*</span>}
                          {field.label}
                        </label>
                        <textarea
                          id={field.name}
                          name={field.name}
                          rows={2}
                          value={fieldValue}
                          placeholder={field.placeholder || field.label}
                          onChange={(e) => handleChange(field.name, e.target.value)}
                          onBlur={() => handleBlur(field.name)}
                          disabled={field.disabled || submitting}
                          className={`w-full px-3 py-1.5 text-sm text-gray-900 bg-white border rounded-md transition-colors focus:outline-hidden focus:ring-2 ${
                            fieldError
                              ? 'border-red-400 focus:ring-red-400/20 focus:border-red-500'
                              : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'
                          } ${field.disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        />
                        {fieldError && <p className="mt-0.5 text-xs font-medium text-red-500">{fieldError}</p>}
                        {field.helperText && !fieldError && <p className="mt-0.5 text-xs text-gray-500">{field.helperText}</p>}
                      </div>
                    );
                  }

                  // Multiple Select
                  if (field.type === 'select_multiple' || (field.type === 'select' && field.multiple)) {
                    const selectedValues: any[] = Array.isArray(fieldValue) ? fieldValue : [];
                    return (
                      <div key={field.name} className={colClass}>
                        <label htmlFor={field.name} className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
                          {isRequired && <span className="text-red-500 mr-1">*</span>}
                          {field.label}
                        </label>
                        <select
                          id={field.name}
                          name={field.name}
                          multiple
                          value={selectedValues.map(String)}
                          onChange={(e) => {
                            const target = e.target as HTMLSelectElement;
                            let selected: string[] = [];
                            if (target.selectedOptions) {
                              selected = Array.from(target.selectedOptions, (option: any) => option.value);
                            } else if (target.value) {
                              selected = [target.value];
                            }
                            handleChange(field.name, selected);
                          }}
                          onBlur={() => handleBlur(field.name)}
                          disabled={field.disabled || submitting}
                          size={Math.min(Math.max((field.options || []).length, 3), 6)}
                          className={`w-full px-3 py-1.5 text-sm text-gray-900 bg-white border rounded-md transition-colors focus:outline-hidden focus:ring-2 ${
                            fieldError
                              ? 'border-red-400 focus:ring-red-400/20 focus:border-red-500'
                              : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'
                          } ${field.disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        >
                          {field.options &&
                            field.options.map((opt, i) => (
                              <option key={i} value={opt.value} className="py-1 px-1">
                                {opt.label}
                              </option>
                            ))}
                        </select>
                        <span className="text-[11px] text-gray-400 mt-1 block">Presione Ctrl (o Cmd en Mac) para seleccionar múltiples opciones</span>
                        {fieldError && <p className="mt-0.5 text-xs font-medium text-red-500">{fieldError}</p>}
                        {field.helperText && !fieldError && <p className="mt-0.5 text-xs text-gray-500">{field.helperText}</p>}

                        {/* Selected Items Chips */}
                        {selectedValues.length > 0 && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-gray-500">
                                Seleccionados ({selectedValues.length}):
                              </span>
                              <button
                                type="button"
                                onClick={() => handleChange(field.name, [])}
                                disabled={field.disabled || submitting}
                                className="text-[11px] font-medium text-red-600 hover:text-red-700 hover:underline cursor-pointer disabled:opacity-50"
                              >
                                Quitar todos
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-1.5 p-1.5 bg-gray-50 border border-gray-200 rounded-md min-h-[36px] max-h-32 overflow-y-auto">
                              {selectedValues.map((val) => {
                                const option = field.options?.find((opt) => String(opt.value) === String(val));
                                const label = field.getOptionLabel
                                  ? field.getOptionLabel(val)
                                  : (option ? option.label : String(val));
                                return (
                                  <span
                                    key={String(val)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md shadow-2xs transition-colors"
                                  >
                                    <span>{label}</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const next = selectedValues.filter((v) => String(v) !== String(val));
                                        handleChange(field.name, next);
                                      }}
                                      disabled={field.disabled || submitting}
                                      aria-label={`Eliminar ${label}`}
                                      className="w-3.5 h-3.5 inline-flex items-center justify-center rounded-full text-blue-500 hover:bg-blue-200 hover:text-blue-800 transition-colors cursor-pointer"
                                    >
                                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Autocomplete
                  if (field.type === 'autocomplete') {
                    return (
                      <AutocompleteField
                        key={field.name}
                        field={field}
                        value={fieldValue}
                        error={fieldError}
                        disabled={field.disabled}
                        submitting={submitting}
                        onChange={(val) => handleChange(field.name, val)}
                        onBlur={() => handleBlur(field.name)}
                        isRequired={isRequired}
                        colClass={colClass}
                      />
                    );
                  }

                  // Select
                  if (field.type === 'select') {
                    return (
                      <div key={field.name} className={colClass}>
                        <label htmlFor={field.name} className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
                          {isRequired && <span className="text-red-500 mr-1">*</span>}
                          {field.label}
                        </label>
                        <select
                          id={field.name}
                          name={field.name}
                          value={fieldValue}
                          onChange={(e) => handleChange(field.name, e.target.value)}
                          onBlur={() => handleBlur(field.name)}
                          disabled={field.disabled || submitting}
                          className={`w-full px-3 py-1.5 min-h-[38px] text-sm text-gray-900 bg-white border rounded-md transition-colors focus:outline-hidden focus:ring-2 ${
                            fieldError
                              ? 'border-red-400 focus:ring-red-400/20 focus:border-red-500'
                              : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'
                          } ${field.disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        >
                          <option value="">Seleccione una opción</option>
                          {field.options &&
                            field.options.map((opt, i) => (
                              <option key={i} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                        </select>
                        {fieldError && <p className="mt-0.5 text-xs font-medium text-red-500">{fieldError}</p>}
                        {field.helperText && !fieldError && <p className="mt-0.5 text-xs text-gray-500">{field.helperText}</p>}
                      </div>
                    );
                  }

                  // Default Text / Number / Email / Date
                  return (
                    <div key={field.name} className={colClass}>
                      <label htmlFor={field.name} className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
                        {isRequired && <span className="text-red-500 mr-1">*</span>}
                        {field.label}
                      </label>
                      <input
                        id={field.name}
                        name={field.name}
                        type={field.type || 'text'}
                        value={fieldValue}
                        placeholder={field.placeholder || field.label}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        onBlur={() => handleBlur(field.name)}
                        disabled={field.disabled || submitting}
                        className={`w-full px-3 py-1.5 min-h-[38px] text-sm text-gray-900 bg-white border rounded-md transition-colors focus:outline-hidden focus:ring-2 ${
                          fieldError
                            ? 'border-red-400 focus:ring-red-400/20 focus:border-red-500'
                            : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'
                        } ${field.disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                      />
                      {fieldError && <p className="mt-0.5 text-xs font-medium text-red-500">{fieldError}</p>}
                      {field.helperText && !fieldError && <p className="mt-0.5 text-xs text-gray-500">{field.helperText}</p>}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Form Action Buttons */}
          <div className="mt-2.5 pt-2 border-t border-gray-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleCancelClick}
              disabled={submitting}
              className="px-3.5 py-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 bg-white hover:bg-gray-50 border border-gray-200 rounded-md shadow-2xs transition-colors min-h-[34px] cursor-pointer disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-1.5 px-5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-md shadow-2xs transition-colors min-h-[34px] cursor-pointer disabled:opacity-60"
            >
              {submitting && (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              <span>{submitButtonText || (resolvedId ? 'Actualizar' : 'Guardar')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CatalogForm;
